import { useCustomFont } from 'hooks/useCustomFont';
import { useUser } from 'modules/User';
import { MutableRefObject, useRef } from 'react';
import { CustomFontState } from 'types/customFont.types';

interface LoadedFont {
  [key: string]: boolean | undefined;
}

interface useLoadBrandFontsProps {
  customFontsMapRef: MutableRefObject<Map<string, CustomFontState>>;
  onBrandLoaded: (selectedBrand: string[]) => void;
  loadFonts: (fonts: string[], customFontsMap: Map<string, CustomFontState>) => Promise<unknown>;
  selectedBrandFonts: string[];
}

// when the font/brand data has been loaded
// - load the (3) selected brands fonts
// - populate the customFontsMapRef with the custom fonts config
export const useLoadBrandFonts = ({
  customFontsMapRef,
  onBrandLoaded,
  loadFonts,
  selectedBrandFonts = [],
}: useLoadBrandFontsProps) => {
  const { userId } = useUser();
  const { data: customFontData = [] } = useCustomFont({ userId });

  // keep track of which fonts have been loaded when the brand / custom font data changes
  // by combining the selected brand fonts & custom fonts into a key
  const loadedBrandFonts = useRef<LoadedFont>({});
  const loadedCustomFonts = useRef<LoadedFont>({});

  const hasCustomFonts = !!customFontData.length;

  // if this key changes it means that the brand data has been loaded & the fonts should be loaded
  // e.g. 'fontFamily1,fontFamily2'
  let customFontKey = '';
  const selectedBrandFontKey = selectedBrandFonts.join(',');

  // populate the customFontsMapRef with the custom fonts
  // & create a key for the custom fonts
  customFontData?.forEach((font) => {
    customFontKey += font.font_name;
    customFontsMapRef.current.set(font.font_name, font);
  });

  // when the brand / custom font data has been loaded - load the selected brands fonts
  const shouldLoadBrandFonts = selectedBrandFontKey && !loadedBrandFonts.current[selectedBrandFontKey];
  const shouldLoadCustomFonts = hasCustomFonts && customFontKey && !loadedCustomFonts.current[customFontKey];

  if (shouldLoadBrandFonts || shouldLoadCustomFonts) {
    // its ok to pass the same font multiple times here - it will only load once
    loadFonts(selectedBrandFonts, customFontsMapRef.current);
    onBrandLoaded(selectedBrandFonts);

    if (shouldLoadBrandFonts) loadedBrandFonts.current[selectedBrandFontKey] = true;
    if (shouldLoadCustomFonts) loadedCustomFonts.current[customFontKey] = true;
  }

  return null;
};
